// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { Button, Modal } from '@imprivata-cloud/components';
import { HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import EmptyStateIllustration from '../../../../assets/empty-list.svg';
import '../empty/ApplicationsEmpty.less';
import { AppProfilesImportButton } from './AppProfilesImportButton';
import { TRANSLATION_ROOT } from './AppProfiles.types';

// --- AppProfilesEmptyState ---

/**
 * Rendered instead of the table when the tenant has zero app profiles.
 * Matches `ApplicationsEmpty`'s illustration/copy/link visual pattern used
 * elsewhere on this same page for the Hyperdrive section.
 */
export const AppProfilesEmptyState = () => {
  const { t } = useTranslation();

  return (
    <div className="applications-empty" data-testid="app-profiles-empty-state">
      <img src={EmptyStateIllustration} alt="" />
      <p>{t(`${TRANSLATION_ROOT}.empty-state-text`)}</p>
      <AppProfilesImportButton>
        <Button type="link">{t(`${TRANSLATION_ROOT}.empty-state-link`)}</Button>
      </AppProfilesImportButton>
    </div>
  );
};

// --- AppProfilesDeleteModal ---

interface AppProfilesDeleteModalProps {
  open: boolean;
  selectedCount: number;
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Bulk-delete confirmation. States the exact count and that deletion
 * cannot be undone. Knows nothing about which ids are selected —
 * the container passes only the count and owns the actual delete call.
 *
 * Uses the same icon+heading+message-as-children, okText/cancelText shape
 * as `IdentityUsers.tsx`/`UserListRowMenu.tsx`'s delete-confirmation modals
 * rather than a hand-rolled footer — that shared `Modal` centers its
 * content and gives Cancel/Remove the correct default/primary treatment
 * for free, which the previous bespoke footer got backwards (Remove was
 * the outline button, Cancel was the filled one).
 */
export const AppProfilesDeleteModal = ({
  open,
  selectedCount,
  isLoading,
  onConfirm,
  onCancel,
}: AppProfilesDeleteModalProps) => {
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      centered={true}
      onOk={onConfirm}
      okText={t(`${TRANSLATION_ROOT}.delete.confirm-button`)}
      okButtonProps={{ disabled: isLoading, loading: isLoading }}
      onCancel={onCancel}
      cancelText={t(`${TRANSLATION_ROOT}.delete.cancel-button`)}
      cancelButtonProps={{ disabled: isLoading }}
      maskClosable={false}
      className="app-profiles-delete-modal"
    >
      <HelpCircle className="app-profiles-delete-alert-icon" size={64} strokeWidth={2} />
      <h3>{t(`${TRANSLATION_ROOT}.delete.confirm-title`, { count: selectedCount })}</h3>
      <p>{t(`${TRANSLATION_ROOT}.delete.confirm-message`, { count: selectedCount })}</p>
    </Modal>
  );
};
